import { useCallback, useEffect, useState } from 'react';

import { ErrorCodes } from '@aline/common';
import { link } from '@aline/routes';
import { HocuspocusProvider, HocuspocusProviderWebsocket } from '@hocuspocus/provider';
import { useNavigate } from 'react-router-dom';
import { getWebsocketConnectionParams } from 'src/auth';
import { collabEndpoint } from 'src/config';
import { useEditorTabStateActionsForTabId } from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import { apolloClient } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useDocumentParams } from 'src/helpers';
import { useGetCurrentDoc } from 'src/hooks';
import { useCurrentTabIdOrThrow } from 'src/layouts/tabs/tab-active-context';
import { pushNotification } from 'src/notifications';
import { Awareness } from 'y-protocols/awareness';
import * as Y from 'yjs';

import { createCollabSessionRecovery } from './collab-session-recovery';
import { createUnsyncedChangesHandler } from './create-unsynced-changes-handler';
import { useStable } from './useStable';

export function useCollabDocument() {
  const navigate = useNavigate();
  const tabId = useCurrentTabIdOrThrow();
  const { document } = useGetCurrentDoc();
  const { documentId } = useDocumentParams();
  const {
    viewer: { setSyncingDoc },
  } = useEditorTabStateActionsForTabId(tabId);
  const handleUnsyncedChanges = useStable(
    () => createUnsyncedChangesHandler(setSyncingDoc),
    [setSyncingDoc]
  );
  const [connected, setConnected] = useState(false);
  // Create doc outside provider so it survives re-connects without losing pending local edits and remote state.
  const doc = useStable(() => new Y.Doc(), [documentId]);
  const socket = useStable(() => {
    return new HocuspocusProviderWebsocket({ url: collabEndpoint });
  }, []);
  const sessionRecovery = useStable(
    () =>
      createCollabSessionRecovery({
        onSessionLost: notifyAuthenticationFailed,
        refreshSession: refreshApiSession,
        socket,
      }),
    [socket]
  );
  const { editor, provider, sharedRoot } = useStable(() => {
    const awareness = new Awareness(doc);
    const sharedRoot = doc.get('content', Y.XmlText);
    if (!(sharedRoot instanceof Y.XmlText)) throw new Error('Expected Y.XmlText.');

    const editor = ClientTransforms.createClientEditor({
      awareness,
      client: apolloClient,
      collaborative: true,
      documentId,
      sharedRoot,
    });
    const provider = new HocuspocusProvider({
      awareness,
      document: doc,
      name: documentId,
      onAuthenticated: () => sessionRecovery.markAuthenticated(),
      onAuthenticationFailed: ({ reason }) => {
        if (reason === ErrorCodes.WS_SESSION_EXPIRED) {
          void sessionRecovery.reconnectWithFreshSession();
          return;
        }

        // Stop reconnecting with a token collab has already rejected.
        sessionRecovery.stop();
      },
      onClose: async ({ event }) => {
        if (event.reason === ErrorCodes.WS_SESSION_EXPIRED) {
          await sessionRecovery.reconnectWithFreshSession();
          return;
        }
        if (event.reason === ErrorCodes.NO_DOCUMENT_ACCESS)
          return navigate(link.RequestDocAccess({ documentId }));
      },
      onConnect: () => setConnected(true),
      onDisconnect: () => {
        setConnected(false);
      },
      token: async () => {
        /**
         * Send WorkOS cookie directly to collab service for authentication.
         * The collab service will verify the cookie using WorkOS SDK.
         * This provides unified session management - revoking the WorkOS session
         * will immediately disconnect collab connections.
         *
         * An authenticated provider is only asked for a token when collab found the one it holds
         * stale, so refresh the cookie through the API first rather than resend an expired one.
         */
        if (provider.isAuthenticated) await sessionRecovery.refreshSession();

        const auth = getWebsocketConnectionParams();
        if (!auth) return '';

        // Encode the auth object as JSON to send to collab.
        return JSON.stringify(auth);
      },
      websocketProvider: socket,
    });

    return { editor, provider, sharedRoot };
  }, [doc, sessionRecovery]);

  useEffect(() => {
    switch (document?.latestVersion.__typename) {
      case 'DocumentVersion': {
        provider.attach();
        break;
      }
      case 'StaticFileVersion': {
        provider.detach();
        break;
      }
    }
  }, [document?.latestVersion.__typename, provider]);

  const destroyProvider = useCallback(
    (providerToDestroy = provider) => {
      providerToDestroy.detach();
      providerToDestroy.awareness?.destroy();
    },
    [provider]
  );

  useEffect(() => {
    provider.on('unsyncedChanges', handleUnsyncedChanges);

    return () => {
      provider.off('unsyncedChanges', handleUnsyncedChanges);
    };
  }, [handleUnsyncedChanges, provider]);

  return { connected, destroyProvider, doc, editor, provider, sharedRoot };
}

function notifyAuthenticationFailed() {
  pushNotification({
    autoHideDuration: 10000,
    message: 'Websocket authentication failed. Contact support for assistance or try and refresh.',
    variant: 'error',
  });
}

async function refreshApiSession() {
  await apolloClient.query({
    fetchPolicy: 'network-only',
    query: Apollo.CurrentUserDocument,
  });
}
